import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, AvatarGroup, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, MenuItem, Pagination,
  Skeleton, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdClose, MdDeleteOutline, MdEdit, MdInventory2, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { deleteData, getData, postData, putData } from '../../Webservice/Webservice';
import { API_URL } from '../../Utils/url';

const FONT = 'Mulish, sans-serif';
const LIMIT = 10;
const TYPES = [
  { value: 'size', label: 'Size', eg: '250 ml' }, { value: 'color', label: 'Color', eg: 'Blue' }, { value: 'shade', label: 'Shade', eg: 'Natural beige' },
  { value: 'fragrance', label: 'Fragrance', eg: 'Rose' }, { value: 'pack_size', label: 'Pack size', eg: 'Pack of 3' }, { value: 'flavor', label: 'Flavor', eg: 'Strawberry' },
  { value: 'formula', label: 'Formula', eg: 'Anti-dandruff' }, { value: 'other', label: 'Other', eg: '' },
];
const typeLabel = (v) => TYPES.find((t) => t.value === v)?.label || v;

const authToken = () => {
  const raw = sessionStorage.getItem('commonToken') || sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const img = (p) => (p ? (p.startsWith('http') ? p : `${API_URL.replace(/\/api\/?$/, '/')}${p.replace(/^\//, '')}`) : undefined);
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const attrsOf = (a) => (a instanceof Map ? Object.fromEntries(a) : a || {});
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' }, '&.Mui-disabled': { background: '#f1f5f9', color: '#94a3b8', boxShadow: 'none' } };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });

const Kpi = ({ title, value, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
  </Box>
);

const Card = ({ title, hint, accent, children, right }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: hint ? 0 : 1 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}>{title}</Typography>
      {right}
    </Box>
    {hint && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1.25 }}>{hint}</Typography>}
    {children}
  </Box>
);

const StatusChip = ({ active }) => (
  <Chip size="small" label={active ? 'Active' : 'Inactive'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: active ? '#dcfce7' : '#f1f5f9', color: active ? '#166534' : '#475569' }} />
);

const emptyForm = { id: null, groupName: '', types: [], items: [], isActive: true };

function VariationGroups() {
  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, products: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const seq = useRef(0);

  const [viewing, setViewing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [pq, setPq] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, status, variationType: type };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('variation-group/list', params, authToken(), false);
      if (mine !== seq.current) return;
      setGroups(res?.groups || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load variation groups.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, status, type]);
  useEffect(() => { load(); }, [load]);

  // product search inside the form
  useEffect(() => {
    if (!formOpen || pq.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await getData('variation-group/search-products', { search: pq.trim(), excludeGroup: form.id || undefined }, authToken(), false);
        setResults(res?.products || []);
      } catch { setResults([]); } finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [pq, formOpen, form.id]);

  const dirty = !!search || !!status || !!type;

  // ---------- form ----------
  const openForm = (g = null) => {
    setViewing(null);
    setErrors([]);
    setFormError('');
    setPq('');
    setResults([]);
    if (g) {
      setForm({
        id: g._id, groupName: g.groupName, types: g.variationTypes || [], isActive: g.isActive !== false,
        items: (g.products || []).filter((p) => p.productId && typeof p.productId === 'object').map((p) => ({ product: p.productId, attributes: { ...attrsOf(p.attributes) } })),
      });
    } else setForm(emptyForm);
    setFormOpen(true);
  };
  const toggleType = (t) => setForm((f) => {
    const types = f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t];
    return { ...f, types, items: f.items.map((it) => ({ ...it, attributes: Object.fromEntries(types.map((k) => [k, it.attributes[k] || ''])) })) };
  });
  const addProduct = (p) => {
    if (!p || form.items.some((it) => it.product._id === p._id)) return;
    setForm((f) => ({ ...f, items: [...f.items, { product: p, attributes: Object.fromEntries(f.types.map((k) => [k, ''])) }] }));
    setPq('');
    setResults([]);
  };
  const setAttr = (idx, key, value) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, attributes: { ...it.attributes, [key]: value } } : it)) }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // two products with the same combination of values
  const duplicates = useMemo(() => {
    const seen = new Map();
    const dup = new Set();
    form.items.forEach((it, i) => {
      if (form.types.some((t) => !String(it.attributes[t] || '').trim())) return;
      const key = form.types.map((t) => String(it.attributes[t]).trim().toLowerCase()).join('|');
      if (seen.has(key)) { dup.add(i); dup.add(seen.get(key)); } else seen.set(key, i);
    });
    return dup;
  }, [form.items, form.types]);

  const save = async () => {
    setFormError('');
    const list = [];
    if (!form.groupName.trim()) list.push('Give the group a name.');
    if (form.types.length === 0) list.push('Choose at least one variation type.');
    if (form.items.length < 2) list.push('Add at least 2 products.');
    form.items.forEach((it) => {
      const missing = form.types.filter((t) => !String(it.attributes[t] || '').trim());
      if (missing.length) list.push(`"${it.product.title}": fill in ${missing.map(typeLabel).join(', ')}.`);
    });
    if (duplicates.size) list.push('Some products have identical values. Each variation must be different.');
    setErrors(list);
    if (list.length) return;
    setSaving(true);
    try {
      const payload = {
        groupName: form.groupName.trim(), variationTypes: form.types,
        products: form.items.map((it) => ({ productId: it.product._id, attributes: Object.fromEntries(form.types.map((t) => [t, String(it.attributes[t]).trim()])) })),
      };
      if (form.id) await putData(`variation-group/update/${form.id}`, payload, authToken(), true);
      else await postData('variation-group/create', payload, authToken(), true);
      setFormOpen(false);
      setNotice(form.id ? 'Group updated.' : 'Group created.');
      load();
    } catch (err) {
      setFormError(err?.message || 'Could not save the group.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (g) => {
    try {
      await putData(`variation-group/update/${g._id}`, { isActive: !g.isActive }, authToken(), true);
      load();
    } catch { /* the service shows the error */ }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteData(`variation-group/delete/${toDelete._id}`, authToken());
      setToDelete(null);
      setNotice('Group deleted. Its products are untouched.');
      if (groups.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the group.');
    } finally {
      setDeleting(false);
    }
  };

  const typeEg = (t) => TYPES.find((x) => x.value === t)?.eg || '';
  const productList = (g) => (g.products || []).filter((p) => p.productId && typeof p.productId === 'object');

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Variation groups</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Link products that are the same item in different sizes, colours or packs, so customers can switch between them.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
          <Button startIcon={<MdAdd />} variant="contained" onClick={() => openForm()} sx={yellowBtn}>Create group</Button>
        </Box>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="All groups" value={stats.total} from="#6366f1" to="#4338ca" active={!status} onClick={() => { setStatus(''); setPage(1); }} />
        <Kpi title="Active" value={stats.active} from="#22c55e" to="#15803d" active={status === 'active'} onClick={() => { setStatus(status === 'active' ? '' : 'active'); setPage(1); }} />
        <Kpi title="Inactive" value={stats.inactive} from="#94a3b8" to="#475569" active={status === 'inactive'} onClick={() => { setStatus(status === 'inactive' ? '' : 'inactive'); setPage(1); }} />
        <Kpi title="Products linked" value={stats.products} from="#0ea5e9" to="#0369a1" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search group or product name…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Variation type" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} sx={{ minWidth: 170 }}>
          <MenuItem value="">All types</MenuItem>
          {TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        {dirty && <Button onClick={() => { setSearch(''); setStatus(''); setType(''); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Group</TableCell>
                <TableCell sx={head}>Varies by</TableCell>
                <TableCell sx={head}>Products</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(5)].map((_, i) => <TableRow key={i}>{[...Array(5)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && groups.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>{dirty ? 'No groups match these filters.' : 'No variation groups yet. Create the first one.'}</TableCell></TableRow>}
              {!loading && groups.map((g) => {
                const list = productList(g);
                return (
                  <TableRow key={g._id} hover>
                    <TableCell sx={{ ...cell, maxWidth: 260 }}>
                      <Typography noWrap onClick={() => setViewing(g)} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, cursor: 'pointer', '&:hover': { color: '#4338ca' } }} title={g.groupName}>{g.groupName}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{(g.products || []).length} variation{(g.products || []).length === 1 ? '' : 's'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(g.variationTypes || []).map((t) => <Chip key={t} size="small" label={typeLabel(t)} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: '#fef3c7', color: '#92400e' }} />)}
                      </Box>
                    </TableCell>
                    <TableCell sx={cell}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 30, height: 30, fontSize: 12 } }}>
                          {list.map((p) => <Avatar key={p.productId._id} variant="rounded" src={img(p.productId.product_images?.[0])}><MdInventory2 size={14} /></Avatar>)}
                        </AvatarGroup>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{list.map((p) => Object.values(attrsOf(p.attributes)).join(' / ')).slice(0, 2).join(', ')}{list.length > 2 ? '…' : ''}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Switch size="small" checked={!!g.isActive} onChange={() => toggleActive(g)} />
                        <StatusChip active={g.isActive} />
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="View"><IconButton size="small" onClick={() => setViewing(g)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openForm(g)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(g); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} group{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* view */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {viewing && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{viewing.groupName}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>Varies by {(viewing.variationTypes || []).map(typeLabel).join(' · ')}</Typography>
              </Box>
              <StatusChip active={viewing.isActive} />
              <IconButton onClick={() => setViewing(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card title={`Variations (${(viewing.products || []).length})`} accent="#f59e0b">
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={head}>Product</TableCell>
                        {(viewing.variationTypes || []).map((t) => <TableCell key={t} sx={head}>{typeLabel(t)}</TableCell>)}
                        <TableCell sx={head} align="right">B2C price</TableCell>
                        <TableCell sx={head}>Stock</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(viewing.products || []).map((p, i) => {
                        const pr = p.productId && typeof p.productId === 'object' ? p.productId : null;
                        const a = attrsOf(p.attributes);
                        return (
                          <TableRow key={pr?._id || i}>
                            <TableCell sx={cell}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Avatar variant="rounded" src={img(pr?.product_images?.[0])} sx={{ width: 36, height: 36 }}><MdInventory2 size={16} /></Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5, maxWidth: 240 }}>{pr?.title || 'Product removed'}</Typography>
                                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{pr?.upc || pr?.productCode || ''}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            {(viewing.variationTypes || []).map((t) => <TableCell key={t} sx={{ ...cell, fontWeight: 700 }}>{a[t] || '—'}</TableCell>)}
                            <TableCell sx={cell} align="right">{pr ? inr(pr.b2c_sellingPrice || pr.b2c_mrp) : '—'}</TableCell>
                            <TableCell>{pr ? <Chip size="small" label={pr.inStock === false || !pr.availableQuantity ? 'Out of stock' : `${pr.availableQuantity} in stock`} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: pr.inStock === false || !pr.availableQuantity ? '#fee2e2' : '#dcfce7', color: pr.inStock === false || !pr.availableQuantity ? '#991b1b' : '#166534' }} /> : '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8' }}>
                Created {viewing.createdAt ? new Date(viewing.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                {viewing.createdBy ? ` by ${`${viewing.createdBy.firstName || ''} ${viewing.createdBy.lastName || ''}`.trim() || viewing.createdBy.email}` : ''}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setViewing(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              <Button onClick={() => openForm(viewing)} startIcon={<MdEdit />} variant="contained" sx={yellowBtn}>Edit group</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* create / edit */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2.25, borderBottom: '3px solid rgb(255 209 2)', background: '#fff' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{form.id ? 'Edit variation group' : 'Create variation group'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{form.items.length} product{form.items.length === 1 ? '' : 's'} · needs at least 2</Typography>
          </Box>
          <IconButton onClick={() => setFormOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card title="Group" hint="What customers choose between" accent="#6366f1">
            <TextField fullWidth size="small" label="Group name *" placeholder="e.g. H&S Anti-dandruff Shampoo" value={form.groupName} onChange={(e) => setForm({ ...form, groupName: e.target.value })} inputProps={{ maxLength: 100 }} />
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#64748b', mt: 2, mb: 0.75 }}>Varies by *</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {TYPES.map((t) => (
                <Chip key={t.value} label={t.label} clickable onClick={() => toggleType(t.value)} sx={{ fontFamily: FONT, fontWeight: 700, border: '1px solid', borderColor: form.types.includes(t.value) ? '#f59e0b' : '#e2e8f0', background: form.types.includes(t.value) ? '#fef3c7' : '#fff', color: form.types.includes(t.value) ? '#92400e' : '#475569' }} />
              ))}
            </Box>
            {form.id && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', mt: 1 }}>Changing the types means filling in the new values for every product below.</Typography>}
          </Card>

          <Card title="Products" hint="Each product needs a different value for every type" accent="#0ea5e9">
            {form.types.length === 0 ? (
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', textAlign: 'center', py: 2 }}>Choose what the group varies by first.</Typography>
            ) : (
              <>
                <Autocomplete
                  freeSolo options={results.filter((r) => !form.items.some((it) => it.product._id === r._id))} loading={searching} inputValue={pq} filterOptions={(x) => x}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.title || '')} getOptionDisabled={(o) => !!o.inGroup}
                  onInputChange={(_, v, reason) => { if (reason !== 'reset') setPq(v); }} onChange={(_, v) => typeof v === 'object' && addProduct(v)}
                  renderOption={(props, o) => (
                    <li {...props} key={o._id}>
                      <Avatar variant="rounded" src={img(o.product_images?.[0])} sx={{ width: 34, height: 34, mr: 1.25 }}><MdInventory2 size={16} /></Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600 }}>{o.title}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{inr(o.b2c_sellingPrice || o.b2c_mrp)}{o.unitValue ? ` · ${o.unitValue}${o.unitName || ''}` : ''}{o.upc ? ` · ${o.upc}` : ''}</Typography>
                      </Box>
                      {o.inGroup && <Chip size="small" label={`In “${o.inGroup}”`} sx={{ ml: 1, height: 20, fontWeight: 700, background: '#fef3c7', color: '#92400e' }} />}
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} size="small" placeholder="Search products by name, UPC or code (2+ letters)…"
                    InputProps={{ ...params.InputProps, endAdornment: <>{searching ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />}
                />
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {form.items.map((it, idx) => (
                    <Box key={it.product._id} sx={{ border: '1px solid', borderColor: duplicates.has(idx) ? '#fca5a5' : '#e2e8f0', borderRadius: '12px', p: 1.5, background: duplicates.has(idx) ? '#fef2f2' : '#fff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                        <Avatar variant="rounded" src={img(it.product.product_images?.[0])} sx={{ width: 40, height: 40 }}><MdInventory2 size={18} /></Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{it.product.title}</Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{inr(it.product.b2c_sellingPrice || it.product.b2c_mrp)}</Typography>
                        </Box>
                        {duplicates.has(idx) && <Chip size="small" label="Same as another" sx={{ height: 20, fontWeight: 700, background: '#fee2e2', color: '#991b1b' }} />}
                        <IconButton size="small" aria-label="remove" onClick={() => removeItem(idx)}><MdClose size={16} /></IconButton>
                      </Box>
                      <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(form.types.length, 3)}, 1fr)` } }}>
                        {form.types.map((t) => (
                          <TextField key={t} size="small" label={typeLabel(t)} placeholder={typeEg(t) ? `e.g. ${typeEg(t)}` : ''} value={it.attributes[t] || ''} onChange={(e) => setAttr(idx, t, e.target.value)} inputProps={{ maxLength: 60 }} />
                        ))}
                      </Box>
                    </Box>
                  ))}
                  {form.items.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', textAlign: 'center', py: 2 }}>Search above and add the products that belong together.</Typography>}
                </Box>
              </>
            )}
          </Card>

          {errors.length > 0 && <Alert severity="error"><ul style={{ margin: 0, paddingLeft: 18 }}>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul></Alert>}
          {formError && <Alert severity="error">{formError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : form.id ? 'Update group' : 'Create group'}</Button>
        </DialogActions>
      </Dialog>

      {/* delete */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete variation group?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}><b>{toDelete?.groupName}</b> is removed and its products stop being linked. The products themselves are not touched. To pause it instead, turn its switch off.</Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VariationGroups;
